import '@warp-ds/icons/elements/job-42';

import pulse from '@finn-no/pulse-sdk';
import { i18n } from '@lingui/core';
import { css, html, LitElement } from 'lit';

const converter = (value) => {
    if (!value) return null;

    try {
        const v = JSON.parse(value);
        if (Array.isArray(v)) {
            return v.map((val) => val.value || val);
            // eslint-disable-next-line no-else-return
        } else if (v.value) {
            return [v.value];
        }
    } catch (err) {
        // noop
    }

    if (typeof value === 'string') {
        return [value];
    }

    return null;
};

class MFPJob extends LitElement {
    static get properties() {
        return {
            market: { attribute: true, type: String },
            baseurl: { attribute: true, type: String },
            css: { attribute: true, converter },
        };
    }

    static get styles() {
        return css`
            *,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0;box-sizing:border-box;border-style:solid;border-width:0;border-color:var(--w-s-color-border,var(--w-s-color-border-default))}html{font-size:62.5%}body{background-color:var(--w-s-color-background,var(--w-s-color-background-default));min-height:100%;margin:0;overflow-y:scroll}body,:host{-webkit-text-size-adjust:100%;tab-size:4;-webkit-tap-highlight-color:transparent;font-family:var(--w-font-family);font-size:var(--w-font-size-m);line-height:var(--w-line-height-m);color:var(--w-s-color-text,var(--w-s-color-text-default))}hr{color:inherit;border-top-width:1px;height:0}abbr:where([title]){-webkit-text-decoration:underline dotted;text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:700}a{cursor:pointer;color:var(--w-s-color-text-link);text-decoration:none}a:hover,a:focus,a:active{text-decoration:underline}b,strong{font-weight:700}code,kbd,samp,pre{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace;font-size:1em}sub,sup{vertical-align:baseline;font-size:75%;line-height:0;position:relative}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;font-weight:inherit;line-height:inherit;color:inherit;margin:0;padding:0}button,select{text-transform:none}button,[type=button],[type=reset],[type=submit]{-webkit-appearance:button}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:baseline}::-webkit-inner-spin-button{height:auto}::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dl,dd,h1,h2,h3,h4,h5,h6,hr,figure,p,pre{margin:0}fieldset{margin:0;padding:0}legend{padding:0}ol,ul,menu{margin:0;padding:0;list-style:none}textarea{resize:vertical}input::placeholder,textarea::placeholder{opacity:1;color:var(--w-s-color-text-placeholder)}button,[role=button]{cursor:pointer}:disabled{cursor:default}img,svg,video,canvas,audio,iframe,embed,object{vertical-align:middle;display:block}img,video{max-width:100%;height:auto}h1{font-size:var(--w-font-size-xxl);line-height:var(--w-line-height-xxl)}h2{font-size:var(--w-font-size-xl);line-height:var(--w-line-height-xl)}h3{font-size:var(--w-font-size-l);line-height:var(--w-line-height-l)}h4{font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}h5{font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}dt,dd{margin:0 16px}h1,h2,h3,h4,h5,ul,ol,dl,p,blockquote{margin:0 0 8px}[hidden]{display:none!important}[tabindex="-1"]:focus:not(:focus-visible){outline:none}legend{float:left;width:100%;margin:0;padding:0;display:table}legend+*{clear:both}fieldset{border:0;min-width:0;margin:0;padding:.01em 0 0}body:not(:-moz-handler-blocked) fieldset{display:table-cell}.page-container{background-color:var(--w-s-color-background);max-width:1010px;margin:0;padding:0 16px}.h2{font-weight:700;font-size:var(--w-font-size-xl);line-height:var(--w-line-height-xl)}.h3{font-weight:700;font-size:var(--w-font-size-l);line-height:var(--w-line-height-l)}.h4{font-weight:700;font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}@media (min-width:1300px){.page-container{margin-left:auto;margin-right:auto;padding-left:31px;padding-right:31px}}.rounded-8{border-radius:8px}.block{display:block}.flex{display:flex}.grid{display:grid}.items-center{align-items:center}.static{position:static}.s-bg-primary-subtle{background-color:var(--w-s-color-background-primary-subtle)}.s-icon-secondary{color:var(--w-s-color-icon-secondary)}.-mx-16{margin-left:-1.6rem;margin-right:-1.6rem}.mb-0{margin-bottom:0}.mb-16{margin-bottom:1.6rem}.mr-8{margin-right:.8rem}.mt-32{margin-top:3.2rem}.p-16{padding:1.6rem}.pt-16{padding-top:1.6rem}.sr-only{clip:rect(0,0,0,0);white-space:nowrap;border-width:0;width:1px;height:1px;margin:-1px;padding:0;position:absolute;overflow:hidden}.focus\\:not-sr-only:focus{clip:auto;white-space:normal;width:auto;height:auto;margin:0;padding:0;position:static;overflow:visible}@media (min-width:768px){.md\\:grid{display:grid}.md\\:col-span-2{grid-column:span 2/span 2}.md\\:grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}};
            :host {
                display: block;
            }
        `;
    }

    connectedCallback() {
        super.connectedCallback();

        const messages = JSON.parse(this.getAttribute('messages') || '{}');
        const locale = document?.documentElement?.lang;
        i18n.load(locale, messages);
        i18n.activate(locale);

        /**
         * @type {Array<{name: string; variant: string;}>}
         */
        const experiments = JSON.parse(
            this.getAttribute('experiments') || '[]',
        );

        pulse.trackPageView({
            object: {
                type: 'MarketFrontpage',
            },
            vertical: {
                name: 'job',
            },
        });

        experiments.forEach((experiment) => {
            pulse.trackExperiment(experiment.name, experiment.variant);
        });

        if (navigator.serviceWorker) {
            navigator.serviceWorker.register('/sw.js').catch(() => {});
        }
    }

    render() {
        return html`
            <a href="#market-start" class="sr-only focus:not-sr-only"
                >${i18n.t({
                    id: 'job.skiplink',
                    message: 'Go to main content',
                    comment: 'skiplink used on the job market frontpage',
                })}</a
            >
            <slot name="topbar"></slot>
            <slot name="banner"></slot>
            <main class="page-container pt-16" style="min-height: 60vw">
                <slot name="broadcast"></slot>
                <header class="flex items-center mb-16">
                    <span class="s-icon-secondary mr-8">
                        <w-icon-job-42></w-icon-job-42>
                    </span>
                    <h1
                        class="h2 mb-0"
                        data-automation-id="marketTitle"
                        id="market-start"
                    >
                        ${this.market}
                    </h1>
                </header>
                <slot name="job-personal-links"></slot>
                <div class="md:grid md:grid-cols-3 -mx-16">
                    <div class="md:col-span-2">
                        <slot name="global-search"></slot>
                        <slot name="job-links"></slot>
                        <slot name="job-profile-entry"></slot>
                    </div>
                    <div>
                        <slot name="last-search"></slot>
                        <div class="mt-32">
                            <slot name="user-accounts"></slot>
                        </div>
                    </div>
                </div>
                <div class="-mx-16">
                    <slot name="employer-profile-recommendations-top"></slot>
                </div>
                <div class="-mx-16">
                    <slot name="recommendations"></slot>
                </div>
                <div class="-mx-16">
                    <slot name="employer-profile-recommendations-bottom"></slot>
                </div>
                <div class="-mx-16">
                    <slot name="cms-articles"></slot>
                </div>
                <section
                    class="s-bg-primary-subtle mb-16 rounded-8 p-16"
                    aria-labeledby="contentTips"
                >
                    <h2 class="h3 mb-16" id="contentTips">
                        ${i18n.t({
                            id: 'job.content-tips.title',
                            message:
                                'More popular searches and useful articles',
                            comment: 'Title for the content tips section',
                        })}
                    </h2>
                    <div class="grid md:grid-cols-3">
                        <div>
                            <h3 class="h4 mb-0">Stillinger</h3>
                            <ul>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?occupation=0.59"
                                        >${i18n.t({
                                            id: 'job.content-tips.link.nurse',
                                            message: 'Nurse',
                                            comment:
                                                'link, links to search for nurse jobs',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?occupation=0.20"
                                        >${i18n.t({
                                            id: 'job.content-tips.link.engineer',
                                            message: 'Engineer',
                                            comment:
                                                'link, links to search for engineer jobs',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?occupation=0.23"
                                        >${i18n.t({
                                            id: 'job.content-tips.link.it',
                                            message: 'IT development',
                                            comment:
                                                'link, links to search for it development jobs',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?occupation=1.16.40"
                                        >${i18n.t({
                                            id: 'job.content-tips.link.educator-therapist',
                                            message:
                                                'Health worker and social educator',
                                            comment:
                                                'link, links to search for it development jobs',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?occupation=0.64"
                                        >${i18n.t({
                                            id: 'job.content-tips.teacher',
                                            message:
                                                'Teacher, headmaster and pedagogue',
                                            comment:
                                                'link, links to search for teacher and equivalent jobs',
                                        })}</a
                                    >
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 class="h4 mb-0">Steder</h3>
                            <ul>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?location=1.20001.20061"
                                        >Oslo</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?location=2.20001.22046.20220"
                                        >Bergen</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?location=2.20001.20016.20318"
                                        >Trondheim</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?location=2.20001.20012.20196"
                                        >Stavanger</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?location=2.20001.22054.20413"
                                        >Tromsø</a
                                    >
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 class="h4 mb-0">Artikler</h3>
                            <ul>
                                <li>
                                    <a href="${this.baseurl}/job/artikler"
                                        >${i18n.t({
                                            id: 'job.articles.list.tips.link',
                                            message:
                                                'Tips and tricks for the job market',
                                            comment:
                                                'link, links to article on the job market',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/artikler/jobbsoknad"
                                        >${i18n.t({
                                            id: 'job.articles.list.application.link',
                                            message:
                                                'Job application, some good tips',
                                            comment:
                                                'link, links to article on tips for the job application',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/artikler/jobbintervju"
                                        >${i18n.t({
                                            id: 'job.articles.list.interview.link',
                                            message:
                                                'Tips to nail the job interview',
                                            comment:
                                                'link, links to article on how to nail the job interview',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/artikler/cv-tips"
                                        >${i18n.t({
                                            id: 'job.articles.list.cv.link',
                                            message: 'Tips for your CV',
                                            comment:
                                                'link, links to article on how to set up your CV',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a href="${this.baseurl}/job/profile/cv"
                                        >${i18n.t({
                                            id: 'job.articles.list.cv.template.link',
                                            message: 'CV-template',
                                            comment:
                                                'link, links to cv-builder landing page',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a href="${this.baseurl}/job/artikler/lonn"
                                        >${i18n.t({
                                            id: 'job.articles.list.salary',
                                            message: 'Salary',
                                            comment:
                                                'link, links to article on salary',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/artikler/karriere"
                                        >${i18n.t({
                                            id: 'job.articles.list.career',
                                            message: 'Career',
                                            comment:
                                                'link, links to article on career',
                                        })}</a
                                    >
                                </li>
                            </ul>
                        </div>
                    </div>
                </section>
            </main>
            <slot name="footer"></slot>
        `;
    }
}

customElements.define('mfp-mc', MFPJob);
